import React, { useEffect, useState } from 'react'
import { toast, ToastContainer } from 'react-toastify'
import Request from '../../../helpers/request'
import * as url from '../../../helpers/url_helper'
import { fwDisplay } from '../../../helpers/fw'

// Format an ISO timestamp in IST for operator display (logs/DB are UTC).
const formatIST = (iso: string | null | undefined): string => {
    if (!iso) return '--'
    try {
        return new Date(iso).toLocaleString('en-IN', {
            dateStyle: 'short', timeStyle: 'short', timeZone: 'Asia/Kolkata',
        }) + ' IST'
    } catch { return String(iso) }
}

const TH = ({ children }: { children: React.ReactNode }) => (
    <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 13, color: '#858D9D', fontWeight: 500, background: '#F9FAFB' }}>
        {children}
    </th>
)

const TD = ({ children }: { children: React.ReactNode }) => (
    <td style={{ padding: '12px 16px', fontSize: 14, color: '#344054', borderTop: '1px solid #E4E7EC' }}>
        {children}
    </td>
)

const Mac = ({ value }: { value: string }) => (
    <code style={{ fontSize: 12 }}>{value || '--'}</code>
)

// onlineStatus: 1 = online, 0 = offline, -1 = never seen. Only 1 is online.
const renderOnline = (onlineStatus: any) => {
    if (onlineStatus === 1) return <span style={{ color: '#16A34A' }}>● Online</span>
    if (onlineStatus === 0) return <span style={{ color: '#9CA3AF' }}>○ Offline</span>
    if (onlineStatus === -1) return <span style={{ color: '#858D9D' }}>Never seen</span>
    return <span style={{ color: '#858D9D' }}>--</span>
}

const EmptyRow = ({ colSpan, children }: { colSpan: number; children: React.ReactNode }) => (
    <tr>
        <td colSpan={colSpan} style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>
            {children}
        </td>
    </tr>
)

// Card shell shared by the three sections: title + subtitle + per-section
// refresh button with its own loading state.
const Section = ({ title, subtitle, loading, onRefresh, refreshTestId, children }: {
    title: string; subtitle: string; loading: boolean; onRefresh: () => void;
    refreshTestId: string; children: React.ReactNode;
}) => (
    <div style={{ background: '#fff', border: '1px solid #E4E7EC', borderRadius: 8, overflow: 'hidden', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '16px 20px', borderBottom: '1px solid #E4E7EC' }}>
            <div style={{ flex: 1 }}>
                <h6 style={{ margin: 0, fontWeight: 600 }}>{title}</h6>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#858D9D' }}>{subtitle}</p>
            </div>
            <button
                data-testid={refreshTestId}
                onClick={onRefresh}
                disabled={loading}
                title="Re-fetch latest data from backend"
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    padding: '3px 10px', fontSize: 12, fontWeight: 500,
                    background: '#fff', color: '#374151',
                    border: '1px solid #D1D5DB', borderRadius: 4,
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1,
                }}
            >
                <i className="bx bx-refresh" style={{ fontSize: 14 }} />
                {loading ? 'Refreshing…' : 'Refresh'}
            </button>
        </div>
        {loading ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#858D9D', fontSize: 14 }}>Loading…</div>
        ) : children}
    </div>
)

const Chip = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <span
        style={{
            display: 'inline-flex', alignItems: 'baseline', gap: 4,
            fontSize: 12, color: '#475467',
            padding: '2px 8px', background: '#F3F4F6', borderRadius: 10,
        }}
    >
        <span style={{ color: '#858D9D' }}>{label}</span>
        <span style={{ fontWeight: 500 }}>{value}</span>
    </span>
)

const StragglerTable = ({ rows, emptyText }: { rows: any[]; emptyText: string }) => (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
            <tr>
                <TH>MAC Address</TH>
                <TH>Product</TH>
                <TH>FW</TH>
                <TH>Target</TH>
                <TH>Last offer</TH>
                <TH>Online</TH>
            </tr>
        </thead>
        <tbody>
            {rows.length === 0 && <EmptyRow colSpan={6}>{emptyText}</EmptyRow>}
            {rows.map((r: any, idx: number) => (
                <tr key={r.macAddress || `row-${idx}`}>
                    <TD><Mac value={r.macAddress} /></TD>
                    <TD>{r.product || '--'}</TD>
                    <TD>{r.firmwareVersion ? fwDisplay(r.firmwareVersion) : '--'}</TD>
                    <TD>{r.target ? fwDisplay(r.target) : '--'}</TD>
                    <TD>
                        {r.sentOTAVersion
                            ? <>{r.sentOTAVersion} <span style={{ color: '#858D9D', fontSize: 12 }}>@ {formatIST(r.sentOTAAt)}</span></>
                            : '--'}
                    </TD>
                    <TD>{renderOnline(r.onlineStatus)}</TD>
                </tr>
            ))}
        </tbody>
    </table>
)

const Ota = () => {
    const [failedData, setFailedData] = useState<any>(null)
    const [failedLoading, setFailedLoading] = useState(false)
    const [statusData, setStatusData] = useState<any>(null)
    const [statusLoading, setStatusLoading] = useState(false)
    const [stragglersData, setStragglersData] = useState<any>(null)
    const [stragglersLoading, setStragglersLoading] = useState(false)

    const fetchFailed = () => {
        setFailedLoading(true)
        Request.post(url.OTA_FAILED, {})
            .then((res: any) => setFailedData(res?.data?.data || null))
            .catch(() => setFailedData(null))
            .finally(() => setFailedLoading(false))
    }

    const fetchStatus = () => {
        setStatusLoading(true)
        Request.post(url.OTA_STATUS, {})
            .then((res: any) => setStatusData(res?.data?.data || null))
            .catch(() => setStatusData(null))
            .finally(() => setStatusLoading(false))
    }

    const fetchStragglers = () => {
        setStragglersLoading(true)
        Request.post(url.OTA_STRAGGLERS, {})
            .then((res: any) => setStragglersData(res?.data?.data || null))
            .catch(() => setStragglersData(null))
            .finally(() => setStragglersLoading(false))
    }

    useEffect(() => {
        fetchFailed()
        fetchStatus()
        fetchStragglers()
    }, [])

    // ── Auto-update opt-in (phase 1, 2026-08-03) ────────────────────────
    // The backend's per-place allow-list. Under mode 'provisioning' a listed
    // place gets FULL auto-OTA (fw-report / reconnect / periodic triggers);
    // everyone else gets provisioning-time OTA only. Phase 2 adds the
    // exemption (deny) list below.
    const [allowPlaceId, setAllowPlaceId] = useState('')
    const [allowMinutes, setAllowMinutes] = useState('')
    const [allowBusy, setAllowBusy] = useState(false)

    const setPlaceAllow = (placeId: string, enabled: boolean, minutes?: number) => {
        const body: any = { placeId: placeId.trim(), enabled }
        if (enabled && minutes && minutes > 0) body.minutes = minutes
        setAllowBusy(true)
        Request.post(url.OTA_PLACE_ALLOW, body)
            .then(() => {
                toast.success(enabled ? 'Auto-update enabled for place' : 'Auto-update disabled for place')
                if (enabled) { setAllowPlaceId(''); setAllowMinutes('') }
                fetchStatus() // the opt-in table renders from ota/status
            })
            .catch((err: any) => {
                // 422 carries field-level messages (e.g. "place not found").
                const errs = err?.response?.data?.errors
                const detail = errs && typeof errs === 'object'
                    ? Object.values(errs).flat().join('; ')
                    : err?.response?.data?.msg
                toast.error(detail || 'Failed to update auto-update opt-in')
            })
            .finally(() => setAllowBusy(false))
    }

    // ── Exempted from auto-update (phase 2, 2026-08-03) ─────────────────
    // The backend's per-place DENY list. Deny is absolute: a listed place
    // receives NO automatic updates in ANY mode — it beats 'on', beats
    // provisioning-time OTA, and beats the opt-in list above. Only a manual
    // admin trigger can still update it (operator intent).
    const [denyPlaceId, setDenyPlaceId] = useState('')
    const [denyMinutes, setDenyMinutes] = useState('')
    const [denyBusy, setDenyBusy] = useState(false)

    const setPlaceDeny = (placeId: string, enabled: boolean, minutes?: number) => {
        const body: any = { placeId: placeId.trim(), enabled }
        if (enabled && minutes && minutes > 0) body.minutes = minutes
        setDenyBusy(true)
        Request.post(url.OTA_PLACE_DENY, body)
            .then(() => {
                toast.success(enabled ? 'Place exempted from auto-update' : 'Exemption removed for place')
                if (enabled) { setDenyPlaceId(''); setDenyMinutes('') }
                fetchStatus() // the exemption table renders from ota/status
            })
            .catch((err: any) => {
                // 422 carries field-level messages (e.g. "place not found").
                const errs = err?.response?.data?.errors
                const detail = errs && typeof errs === 'object'
                    ? Object.values(errs).flat().join('; ')
                    : err?.response?.data?.msg
                toast.error(detail || 'Failed to update auto-update exemption')
            })
            .finally(() => setDenyBusy(false))
    }

    const failed: any[] = failedData?.failed || []
    const byState: Record<string, number> = statusData?.jobs?.byState || {}
    const active: any[] = statusData?.jobs?.active || []
    const counts = stragglersData?.counts || {}
    const unknownFw: any[] = stragglersData?.unknownFw || []
    const belowLatest: any[] = stragglersData?.belowLatest || []
    const policyMode: string = statusData?.policy?.mode || '--'
    const allowedPlaces: any[] = statusData?.policy?.allowedPlaces || []
    const deniedPlaces: any[] = statusData?.policy?.deniedPlaces || []

    return (
        <div className="page-content" style={{ padding: 30 }}>
            <ToastContainer />
            <h4 style={{ marginBottom: 24, fontWeight: 600 }}>OTA</h4>

            {/* Section 0 — auto-update opt-in places */}
            <Section
                title={`Auto-update opt-in${allowedPlaces.length ? ` — ${allowedPlaces.length} place(s)` : ''}`}
                subtitle="Places that get automatic firmware updates on every trigger (reports, reconnects, periodic sweeps). All other places update at provisioning time only."
                loading={statusLoading}
                onRefresh={fetchStatus}
                refreshTestId="refresh-optin"
            >
                <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '12px 20px 0' }}>
                        <Chip label="Global mode" value={policyMode} />
                        {policyMode === 'on' && (
                            <span style={{ fontSize: 12, color: '#B54708' }}>
                                Mode is &lsquo;on&rsquo; — every place auto-updates; this list has no additional effect.
                            </span>
                        )}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px 20px' }}>
                        <input
                            data-testid="optin-place-input"
                            value={allowPlaceId}
                            onChange={(e) => setAllowPlaceId(e.target.value)}
                            placeholder="Place ID (24-char hex)"
                            style={{ flex: '1 1 260px', maxWidth: 340, padding: '6px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 4, fontFamily: 'monospace' }}
                        />
                        <input
                            data-testid="optin-minutes-input"
                            value={allowMinutes}
                            onChange={(e) => setAllowMinutes(e.target.value.replace(/[^0-9]/g, ''))}
                            placeholder="TTL minutes (blank = permanent)"
                            style={{ flex: '0 1 220px', padding: '6px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 4 }}
                        />
                        <button
                            data-testid="optin-add"
                            disabled={allowBusy || !allowPlaceId.trim()}
                            onClick={() => setPlaceAllow(allowPlaceId, true, allowMinutes ? Number(allowMinutes) : undefined)}
                            style={{
                                padding: '6px 14px', fontSize: 13, fontWeight: 500,
                                background: '#12B76A', color: '#fff', border: 'none', borderRadius: 4,
                                cursor: allowBusy || !allowPlaceId.trim() ? 'not-allowed' : 'pointer',
                                opacity: allowBusy || !allowPlaceId.trim() ? 0.6 : 1,
                            }}
                        >
                            Enable auto-update
                        </button>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <TH>Place ID</TH>
                                <TH>Expires</TH>
                                <TH>Action</TH>
                            </tr>
                        </thead>
                        <tbody>
                            {allowedPlaces.length === 0 && (
                                <EmptyRow colSpan={3}>No places opted in — only provisioning-time OTA is active fleet-wide</EmptyRow>
                            )}
                            {allowedPlaces.map((p: any) => (
                                <tr key={p.placeId}>
                                    <TD><code style={{ fontSize: 12 }}>{p.placeId}</code></TD>
                                    <TD>{p.expiresAt ? formatIST(p.expiresAt) : 'No expiry'}</TD>
                                    <TD>
                                        <button
                                            data-testid={`optin-remove-${p.placeId}`}
                                            disabled={allowBusy}
                                            onClick={() => setPlaceAllow(p.placeId, false)}
                                            style={{
                                                padding: '3px 10px', fontSize: 12, fontWeight: 500,
                                                background: '#fff', color: '#B42318',
                                                border: '1px solid #FDA29B', borderRadius: 4,
                                                cursor: allowBusy ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </TD>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            </Section>

            {/* Section 0b — exempted (deny-listed) places */}
            <Section
                title={`Exempted from auto-update${deniedPlaces.length ? ` — ${deniedPlaces.length} place(s)` : ''}`}
                subtitle="Exempted places receive NO automatic updates in any mode; manual/admin OTA still works."
                loading={statusLoading}
                onRefresh={fetchStatus}
                refreshTestId="refresh-optout"
            >
                <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '12px 20px' }}>
                        <input
                            data-testid="optout-place-input"
                            value={denyPlaceId}
                            onChange={(e) => setDenyPlaceId(e.target.value)}
                            placeholder="Place ID (24-char hex)"
                            style={{ flex: '1 1 260px', maxWidth: 340, padding: '6px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 4, fontFamily: 'monospace' }}
                        />
                        <input
                            data-testid="optout-minutes-input"
                            value={denyMinutes}
                            onChange={(e) => setDenyMinutes(e.target.value.replace(/[^0-9]/g, ''))}
                            placeholder="TTL minutes (blank = permanent)"
                            style={{ flex: '0 1 220px', padding: '6px 10px', fontSize: 13, border: '1px solid #D1D5DB', borderRadius: 4 }}
                        />
                        <button
                            data-testid="optout-add"
                            disabled={denyBusy || !denyPlaceId.trim()}
                            onClick={() => setPlaceDeny(denyPlaceId, true, denyMinutes ? Number(denyMinutes) : undefined)}
                            style={{
                                padding: '6px 14px', fontSize: 13, fontWeight: 500,
                                background: '#B54708', color: '#fff', border: 'none', borderRadius: 4,
                                cursor: denyBusy || !denyPlaceId.trim() ? 'not-allowed' : 'pointer',
                                opacity: denyBusy || !denyPlaceId.trim() ? 0.6 : 1,
                            }}
                        >
                            Exempt from auto-update
                        </button>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <TH>Place ID</TH>
                                <TH>Expires</TH>
                                <TH>Action</TH>
                            </tr>
                        </thead>
                        <tbody>
                            {deniedPlaces.length === 0 && (
                                <EmptyRow colSpan={3}>No exemptions — every place follows the global OTA mode</EmptyRow>
                            )}
                            {deniedPlaces.map((p: any) => (
                                <tr key={p.placeId}>
                                    <TD><code style={{ fontSize: 12 }}>{p.placeId}</code></TD>
                                    <TD>{p.expiresAt ? formatIST(p.expiresAt) : 'No expiry'}</TD>
                                    <TD>
                                        <button
                                            data-testid={`optout-remove-${p.placeId}`}
                                            disabled={denyBusy}
                                            onClick={() => setPlaceDeny(p.placeId, false)}
                                            style={{
                                                padding: '3px 10px', fontSize: 12, fontWeight: 500,
                                                background: '#fff', color: '#B42318',
                                                border: '1px solid #FDA29B', borderRadius: 4,
                                                cursor: denyBusy ? 'not-allowed' : 'pointer',
                                            }}
                                        >
                                            Remove
                                        </button>
                                    </TD>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            </Section>

            {/* Section a — failed verification */}
            <Section
                title={`Couldn't update (failed verification)${failedData?.count != null ? ` — ${failedData.count}` : ''}`}
                subtitle="Modules whose OTA never verified after the retry budget. These need manual attention (reflash / site visit)."
                loading={failedLoading}
                onRefresh={fetchFailed}
                refreshTestId="refresh-failed"
            >
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <TH>MAC Address</TH>
                            <TH>Product</TH>
                            <TH>Target</TH>
                            <TH>Attempts</TH>
                            <TH>Note</TH>
                            <TH>Failed at</TH>
                        </tr>
                    </thead>
                    <tbody>
                        {failed.length === 0 && <EmptyRow colSpan={6}>No failed OTA jobs</EmptyRow>}
                        {failed.map((j: any, idx: number) => (
                            <tr key={j.macAddress || `row-${idx}`}>
                                <TD><Mac value={j.macAddress} /></TD>
                                <TD>{j.product || '--'}</TD>
                                <TD>{j.target ? fwDisplay(j.target) : '--'}</TD>
                                <TD>{j.attempts ?? '--'}</TD>
                                <TD>{j.note || '--'}</TD>
                                <TD>{formatIST(j.failedAt)}</TD>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Section>

            {/* Section b — active verification jobs */}
            <Section
                title="Active verification jobs"
                subtitle="OTA offers currently in flight — the verifier re-checks each module's reported firmware after its next expected heartbeat."
                loading={statusLoading}
                onRefresh={fetchStatus}
                refreshTestId="refresh-status"
            >
                <>
                    {Object.keys(byState).length > 0 && (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '12px 20px 0' }}>
                            {Object.entries(byState).map(([state, count]) => (
                                <Chip key={state} label={state} value={count} />
                            ))}
                        </div>
                    )}
                    <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
                        <thead>
                            <tr>
                                <TH>MAC Address</TH>
                                <TH>Product</TH>
                                <TH>Target</TH>
                                <TH>Attempts</TH>
                                <TH>State</TH>
                                <TH>Next check</TH>
                            </tr>
                        </thead>
                        <tbody>
                            {active.length === 0 && <EmptyRow colSpan={6}>No active verification jobs</EmptyRow>}
                            {active.map((j: any, idx: number) => (
                                <tr key={j.macAddress || `row-${idx}`}>
                                    <TD><Mac value={j.macAddress} /></TD>
                                    <TD>{j.product || '--'}</TD>
                                    <TD>{j.target ? fwDisplay(j.target) : '--'}</TD>
                                    <TD>{j.attempts ?? '--'}</TD>
                                    <TD>{j.state || '--'}</TD>
                                    <TD>{formatIST(j.verifyAfter)}</TD>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </>
            </Section>

            {/* Section c — stragglers */}
            <Section
                title="Stragglers"
                subtitle="Modules that never picked up the latest firmware — either the backend doesn't know their FW at all, or they report a version below the latest release."
                loading={stragglersLoading}
                onRefresh={fetchStragglers}
                refreshTestId="refresh-stragglers"
            >
                <>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '12px 20px 0' }}>
                        <Chip label="Unknown FW" value={counts.unknownFw ?? '--'} />
                        <Chip label="Below latest" value={counts.belowLatest ?? '--'} />
                        <Chip label="Scanned" value={counts.scanned ?? '--'} />
                    </div>
                    <div style={{ padding: '12px 20px 4px', fontSize: 13, fontWeight: 600, color: '#344054' }}>Unknown FW</div>
                    <StragglerTable rows={unknownFw} emptyText="No unknown-FW modules" />
                    <div style={{ padding: '16px 20px 4px', fontSize: 13, fontWeight: 600, color: '#344054', borderTop: '1px solid #E4E7EC' }}>Below latest</div>
                    <StragglerTable rows={belowLatest} emptyText="No below-latest modules" />
                </>
            </Section>
        </div>
    )
}

export default Ota
